import {useState, useCallback, useEffect, useRef} from 'react';

import {DebugPage} from './debug-page';

type Props = {
    originalUrl: string;
    title: string;
    subtitle: string;
};

/**
 * dangerouslySetInnerHTML does not execute <script> tags. This function
 * finds all scripts in a container and re-creates them as proper DOM
 * elements so the browser evaluates them.
 */
function executeScripts(container: HTMLElement): void {
    const scripts = container.querySelectorAll('script');
    scripts.forEach((oldScript) => {
        const newScript = document.createElement('script');
        Array.from(oldScript.attributes).forEach((attr) => {
            newScript.setAttribute(attr.name, attr.value);
        });
        newScript.textContent = oldScript.textContent;
        oldScript.parentNode?.replaceChild(newScript, oldScript);
    });
}

/**
 * Phtml pages designed to run standalone often include global CSS rules
 * targeting html/body (e.g. `html, body { overflow: auto !important }`)
 * that break the flex layout of PageWrapper / SecondarySidebar when
 * injected into the debug shell. This strips those rules from <style>
 * blocks before injection.
 */
function sanitizeGlobalStyles(html: string): string {
    return html.replace(/<style[\s\S]*?<\/style>/gi, (styleBlock) => {
        return styleBlock.replace(/(?:html|body)(?:\s*,\s*(?:html|body))*\s*\{[^}]*\}/gi, '');
    });
}

export function PhtmlDebugPage(props: Props): React.ReactElement {
    const {originalUrl, title, subtitle} = props;
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const fetchMarkup = useCallback(() => {
        setIsLoading(true);
        setError(null);

        if (containerRef.current) {
            containerRef.current.innerHTML = '';
        }

        const stripped = originalUrl.replace(/\/$/, '');
        const qIndex = stripped.indexOf('?');
        const url =
            qIndex === -1
                ? `${stripped}.webchunk`
                : `${stripped.slice(0, qIndex)}.webchunk${stripped.slice(qIndex)}`;

        fetch(url, {
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
                accept: '*/*',
            },
            credentials: 'same-origin',
            redirect: 'error',
        })
            .then((res) => {
                if (!res.ok) {
                    throw new Error(`Server returned ${res.status}`);
                }

                return res.text();
            })
            .then((body) => {
                const container = containerRef.current;
                if (container) {
                    container.innerHTML = sanitizeGlobalStyles(body);
                    executeScripts(container);
                    Nut.updateAppearances();
                    Nut.preparePage();
                }
                setIsLoading(false);
            })
            .catch((err: Error) => {
                const isRedirect = err instanceof TypeError && err.message.includes('redirect');
                const message = isRedirect
                    ? 'This page redirected. It may require the aiDebugging capability or other permissions.'
                    : err.message || 'Failed to load page content.';
                setError(message);
                setIsLoading(false);
            });
    }, [originalUrl]);

    useEffect(() => {
        fetchMarkup();
    }, [fetchMarkup]);

    return (
        <DebugPage title={title} subtitle={subtitle} error={error} onRefresh={fetchMarkup}>
            {isLoading && (
                <div style={{padding: 32, textAlign: 'center', color: '#6c757d'}}>Loading...</div>
            )}
            <div ref={containerRef} />
        </DebugPage>
    );
}
